import { z } from 'zod';

import type { InboxListParams } from '../api/conversations-api';

export const INBOX_TABS = ['open', 'pending', 'closed'] as const;
export const INBOX_KINDS = ['chat', 'offline', 'ticket'] as const;
export const INBOX_RATINGS = ['good', 'neutral', 'bad', 'none'] as const;
export const INBOX_PAGE_SIZE = 30;
export const INBOX_VIEWS = ['conversations', 'tickets', 'visitors'] as const;

/**
 * URL search of the inbox: the tab and every filter live here so a link or a reload shows the
 * same list. Shared by the org-wide route and the per-store one; the store route pins `storeId`
 * from its path instead.
 */
export const inboxSearchSchema = z.object({
  /** The left pane: conversations (default), tickets only (LC9) or the live visitor list (LC7). */
  view: z.enum(INBOX_VIEWS).optional().catch(undefined),
  status: z.enum(INBOX_TABS).optional().catch(undefined),
  /** A member id, `me`, or `none` for unassigned. */
  assignedTo: z.string().min(1).max(64).optional().catch(undefined),
  departmentId: z.string().min(1).max(64).optional().catch(undefined),
  tag: z.string().min(1).max(60).optional().catch(undefined),
  rating: z.enum(INBOX_RATINGS).optional().catch(undefined),
  kind: z.enum(INBOX_KINDS).optional().catch(undefined),
  missed: z.boolean().optional().catch(undefined),
  /** Past a first-response or resolution target (tickets), or unanswered too long (chats). */
  slaBreached: z.boolean().optional().catch(undefined),
  /** Comma-separated store ids; ignored on the per-store route. */
  storeId: z.string().min(1).max(1850).optional().catch(undefined),
  q: z.string().min(1).max(120).optional().catch(undefined),
});

export type InboxSearch = z.infer<typeof inboxSearchSchema>;
export type InboxTab = (typeof INBOX_TABS)[number];

export type InboxView = (typeof INBOX_VIEWS)[number];

export const inboxTab = (search: InboxSearch): InboxTab => search.status ?? 'open';
export const inboxView = (search: InboxSearch): InboxView => search.view ?? 'conversations';

/** The stores a list covers: the pinned one, the store filter, or every store the member sees. */
export function scopedStoreIds(
  search: InboxSearch,
  stores: ReadonlyArray<{ id: string }>,
  pinnedStoreId?: string,
): string[] {
  if (pinnedStoreId) return [pinnedStoreId];
  const all = stores.map((store) => store.id);
  const filter = search.storeId?.split(',').filter((id) => all.includes(id));
  return filter && filter.length > 0 ? filter : all;
}

/** The list request for a search; `pinnedStoreId` is the per-store route's store. */
export function inboxListParams(search: InboxSearch, pinnedStoreId?: string): InboxListParams {
  const storeId = pinnedStoreId ?? search.storeId;
  // The Tickets view is the conversation list with the kind pinned.
  const kind = inboxView(search) === 'tickets' ? 'ticket' : search.kind;
  return {
    status: inboxTab(search),
    limit: INBOX_PAGE_SIZE,
    ...(search.assignedTo ? { assignedTo: search.assignedTo } : {}),
    ...(search.departmentId ? { departmentId: search.departmentId } : {}),
    ...(search.tag ? { tag: search.tag } : {}),
    ...(search.rating ? { rating: search.rating } : {}),
    ...(kind ? { kind } : {}),
    ...(search.missed !== undefined ? { missed: search.missed ? 'true' : 'false' } : {}),
    ...(search.slaBreached !== undefined
      ? { slaBreached: search.slaBreached ? 'true' : 'false' }
      : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(storeId ? { storeId } : {}),
  };
}

/** Filters set besides the tab, for the "clear filters" affordance. */
export function activeFilterCount(search: InboxSearch, pinnedStore: boolean): number {
  const keys = [
    'assignedTo',
    'departmentId',
    'tag',
    'rating',
    'missed',
    'slaBreached',
    'q',
  ] as const;
  const kindCounts = search.kind !== undefined && inboxView(search) !== 'tickets';
  return (
    keys.filter((key) => search[key] !== undefined).length +
    (kindCounts ? 1 : 0) +
    (!pinnedStore && search.storeId ? 1 : 0)
  );
}
